import codec from './compressionCodecSource.json';
import compressionWorker from './compressionWorker';
import { decompressLegacyUTF16, parseLegacyJSON } from './legacyDecompression';
import { safeSetItem } from './storageQuotaGuard';

export class CompressionManager {
  constructor() {
    this.worker = null;
    this.ready = null;
    this.url = null;
    this.initTimer = null;
    this.retryAfter = 0;
    this.messageId = 0;
    this.pendingMessages = new Map();
  }

  releaseUrl() {
    if (this.url) URL.revokeObjectURL(this.url);
    this.url = null;
  }

  destroy(error = new Error('Compression worker stopped')) {
    if (this.worker) this.worker.terminate();
    this.worker = null;
    clearTimeout(this.initTimer);
    this.initTimer = null;
    this.releaseUrl();
    if (this.rejectReady) this.rejectReady(error);
    this.rejectReady = null;
    this.ready = null;
    this.retryAfter = Date.now() + 30000;
    this.pendingMessages.forEach(({ reject }) => reject(error));
    this.pendingMessages.clear();
  }

  initWorker() {
    if (this.ready) return this.ready;
    if (typeof Worker === 'undefined' || Date.now() < this.retryAfter) {
      return Promise.reject(new Error('Compression worker unavailable'));
    }
    this.ready = new Promise((resolve, reject) => {
      this.rejectReady = reject;
      try {
        this.url = URL.createObjectURL(new Blob([
          codec.source, '\n;(', compressionWorker.toString(), ')();',
        ], { type: 'text/javascript' }));
        const worker = new Worker(this.url);
        this.worker = worker;
        this.initTimer = setTimeout(() => this.destroy(new Error('Compression worker startup timeout')), 5000);
        worker.onmessage = ({ data }) => {
          if (worker !== this.worker) return;
          if (data.ready) {
            clearTimeout(this.initTimer);
            this.initTimer = null;
            this.rejectReady = null;
            this.releaseUrl();
            resolve(worker);
            return;
          }
          const pending = this.pendingMessages.get(data.id);
          if (!pending) return;
          this.pendingMessages.delete(data.id);
          if (data.success) pending.resolve(data.result);
          else pending.reject(new Error(data.error || 'Compression worker failed'));
        };
        worker.onerror = () => { if (worker === this.worker) this.destroy(new Error('Compression worker failed')); };
        worker.onmessageerror = () => { if (worker === this.worker) this.destroy(new Error('Compression worker response failed')); };
      } catch (error) {
        this.destroy(error);
      }
    });
    const ready = this.ready;
    ready.catch(() => { if (this.ready === ready) this.ready = null; });
    return ready;
  }

  async request(action, data) {
    const worker = await this.initWorker();
    if (worker !== this.worker) throw new Error('Compression worker stopped');
    const id = ++this.messageId;
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => this.destroy(new Error('Compression worker timeout')), 30000);
      this.pendingMessages.set(id, {
        resolve: (result) => { clearTimeout(timeout); resolve(result); },
        reject: (error) => { clearTimeout(timeout); reject(error); },
      });
      try {
        worker.postMessage({ id, action, data });
      } catch (error) {
        this.destroy(error);
      }
    });
  }

  async compress(data) {
    try {
      return await this.request('compress', data);
    } catch (_) {
      return data;
    }
  }

  async decompress(data) {
    try {
      return await this.request('decompress', data);
    } catch (_) {
      const json = await decompressLegacyUTF16(data);
      return parseLegacyJSON(json);
    }
  }
}

const compressionManager = new CompressionManager();

export const compressedStorage = {
  async save(store, key, data) {
    const value = await compressionManager.compress(data);
    const saved = await safeSetItem(store, key, value);
    if (!saved) throw new Error(`[compressedStorage] Failed to save "${key}" — storage quota exceeded after cleanup`);
    await safeSetItem(store, `${key}_meta`, {
      count: Array.isArray(data) ? data.length : 0,
      timestamp: Date.now(),
      compressed: typeof value === 'string',
    });
  },

  async load(store, key) {
    const value = await store.getItem(key);
    if (value == null) return null;
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return null;
    if (/^\s*[[{]/.test(value)) {
      try { return await parseLegacyJSON(value); } catch (_) { /* May be a compressed value. */ }
    }
    try {
      return await compressionManager.decompress(value);
    } catch (_) {
      return null;
    }
  },

  async clear(store, key) {
    await store.removeItem(key);
    await store.removeItem(`${key}_meta`);
  },
};
